import React from "react";

export interface GhostSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: React.ReactNode;
  ariaLabel?: string;
  id?: string;
  size?: "sm" | "md" | "lg";
  variant?: "dark" | "light";
  className?: string;
}

export const GhostSwitch: React.FC<GhostSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  ariaLabel,
  id,
  size = "md",
  variant = "dark",
  className = "",
}) => {
  const switchSizeClass =
    size === "sm"
      ? "w-9 h-5"
      : size === "lg"
      ? "w-14 h-7"
      : "w-11 h-6";

  const knobSizeClass =
    size === "sm"
      ? "after:h-4 after:w-4 after:top-[1px] after:left-[1px] peer-checked:after:translate-x-4"
      : size === "lg"
      ? "after:h-6 after:w-6 after:top-[1px] after:left-[1px] peer-checked:after:translate-x-[26px]"
      : "after:h-5 after:w-5 after:top-[1px] after:left-[1px] peer-checked:after:translate-x-[20px]";

  const isDark = variant === "dark";

  const trackThemeClasses = isDark
    ? "bg-white/10 border-white/15 hover:bg-white/15 peer-checked:bg-blue-600 peer-checked:border-blue-400/80 group-hover:peer-checked:bg-blue-500 group-hover:peer-checked:border-blue-300 after:bg-white/95 after:border-white/40 shadow-[inset_0_0_8px_rgba(255,255,255,0.25)] group-hover:shadow-[inset_0_0_12px_rgba(255,255,255,0.4)] peer-checked:shadow-[inset_0_0_10px_rgba(255,255,255,0.45)] group-hover:peer-checked:shadow-[inset_0_0_18px_rgba(255,255,255,0.85)] after:shadow-[inset_0_0_6px_rgba(255,255,255,0.85)] group-hover:after:shadow-[inset_0_0_10px_rgba(255,255,255,1)]"
    : "bg-slate-200 border-slate-300 hover:bg-slate-300/70 peer-checked:bg-blue-600 peer-checked:border-blue-600 group-hover:peer-checked:bg-blue-500 after:bg-white after:border-slate-200 shadow-[inset_0_0_6px_rgba(255,255,255,0.5)] group-hover:shadow-[inset_0_0_17px_rgba(255,255,255,0.88)] group-hover:peer-checked:shadow-[inset_0_0_17px_rgba(255,255,255,0.88)] after:shadow-[inset_0_0_5px_rgba(255,255,255,0.8)] group-hover:after:shadow-[inset_0_0_8px_rgba(255,255,255,1)]";

  const labelTextClass = isDark ? "text-white/90" : "text-slate-800 font-medium";
  const selectedTrackClasses = checked
    ? "!border-transparent !outline-none !ring-0 shadow-[inset_0_0_17px_rgba(255,255,255,0.94),0_0_9px_rgba(96,165,250,0.45)]"
    : "";

  return (
    <label
      data-ui="ghost-switch"
      data-selected={checked ? "true" : "false"}
      data-theme={variant}
      className={`group relative inline-flex items-center select-none outline-none focus:outline-none rounded-full transition-all duration-300 ${
        disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"
      } ${className}`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer outline-none focus:outline-none"
        aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
      />
      <div
        data-ui="ghost-switch-track"
        className={`relative ${switchSizeClass} border outline-none focus:outline-none focus:ring-0 peer-focus:outline-none peer-focus:ring-0 rounded-full peer ${knobSizeClass} after:content-[''] after:absolute after:rounded-full after:transition-shadow ${trackThemeClasses} ${selectedTrackClasses} transition-[background-color,border-color,box-shadow] duration-300`}
      ></div>
      {label && <span data-ui="ghost-switch-label" className={`ml-3 text-sm transition-colors ${labelTextClass}`}>{label}</span>}
    </label>
  );
};

export default GhostSwitch;
